import type React from "react";
import { Group, NumberInput, Paper, SegmentedControl, Select, Stack } from "@mantine/core";
import { REPAIR_MODULES, type RepairLanguage } from "@models/repairInformation";
import VehiclePicker from "./VehiclePicker";
import { MODULE_LABELS } from "./facts";
import { ALL_MODULES, languagesFor, type RepairSettings, type VehicleList } from "./useRepairInformation";

interface RepairInformationSettingsProps {
  value: RepairSettings;
  onChange: (patch: Partial<RepairSettings>) => void;
  vehicles: VehicleList;
}

/** The repair-information part of the search settings: vehicle (required), scope and minimum score. */
const RepairInformationSettings: React.FC<RepairInformationSettingsProps> = ({ value, onChange, vehicles }) => (
  <Stack gap="sm">
    <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-gray-0)">
      <Stack gap="xs">
        <VehiclePicker
          vehicles={vehicles.vehicles}
          value={value.vehicle}
          onChange={(vehicle) => onChange({ vehicle })}
          loading={vehicles.loading}
          error={vehicles.error}
        />
        <Group gap="xs" wrap="nowrap" align="flex-end">
          <Select
            flex={1}
            size="xs"
            label="Module"
            data={[
              { value: ALL_MODULES, label: "All modules" },
              ...REPAIR_MODULES.map((m) => ({ value: m, label: MODULE_LABELS[m] })),
            ]}
            value={value.module}
            onChange={(module) => onChange({ module: module ?? ALL_MODULES })}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <SegmentedControl
            size="xs"
            aria-label="Language"
            value={value.language}
            onChange={(language) => onChange({ language: language as RepairLanguage })}
            data={languagesFor(value.vehicle).map((l) => ({ value: l, label: l.toUpperCase() }))}
          />
        </Group>
        <NumberInput
          size="xs"
          label="Min score"
          description="Only hits scoring at or above this (0–100) are shown. 0 shows every hit."
          aria-label="Min score"
          value={value.threshold}
          onChange={(threshold) => typeof threshold === "number" && onChange({ threshold })}
          min={0}
          max={100}
          clampBehavior="strict"
        />
      </Stack>
    </Paper>
  </Stack>
);

export default RepairInformationSettings;
